import {lazy, Suspense} from "react";
import {Navigate, Route, Routes} from "react-router-dom";
import {useAtomValue} from "jotai";
import i18next from "i18next";
import dayjs from "dayjs";
import {QueryClientProvider} from "@tanstack/react-query";
import {AdapterFormats, LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import {authenticationLoadable} from "../store/authenticationStore";
import {authenticatedUserLoadable} from "../store/authenticatedUserStore";
import {useAnalyticsInit} from "../hooks/analyticsHooks";
import {InsightsPage} from "./pages/InsightsPage";
import {AdminPage} from "./pages/AdminPage";
import {AdminDashboardView} from "./admin/AdminDashboardView";
import {AdminCompaniesView} from "./admin/AdminCompaniesView";
import {AdminOrdersView} from "./admin/AdminOrdersView";
import {ErrorPage} from "./pages/ErrorPage";
import {CompanyProfileView} from "./companies/CompanyDetailsView";
import {CompanyMembersView} from "./companies/CompanyMembersView";
import {CompanyOrdersView} from "./companies/CompanyOrdersView";
import {AdminUsersView} from "./admin/AdminUsersView";
import {AdminIrriTilesView} from "./admin/AdminIrriTilesView";
import {AdminIrriTileDetailsView} from "./admin/AdminIrriTileDetailsView";
import {LoginPage} from "./pages/LoginPage";
import {RegistrationPage} from "./pages/RegistrationPage";
import {PasswordRestorePage} from "./pages/PasswordRestorePage";
import {OrganizationPage} from "./pages/OrganizationPage";
import {MapPage} from "./pages/MapPage";
import {OrderConfirmationPage} from "./pages/OrderConfirmationPage";
import {AdminFarmsView} from "./admin/AdminFarmsView";
import {UserPage} from "./pages/UserPage";
import {UserDetailsView} from "./users/UserDetailsView";
import {UserCompaniesView} from "./users/UserCompaniesView";
import {UserInvitationPage} from "./pages/UserInvitationPage";
import {UserOrdersView} from "./users/UserOrdersView";
import {CompanyFarmsView} from "./companies/CompanyFarmsView";
import {UserFarmsView} from "./users/UserFarmsView";
import {CompanyInsightsView} from "./insights/CompanyInsightsView";
import {FarmInsightsView} from "./insights/FarmInsightsView";
import {FieldInsightsView} from "./insights/FieldInsightsView";
import {MapView} from "./map/MapView";
import {PasswordResetPage} from "./pages/PasswordResetPage";
import {useSetImpersonation} from "../hooks/impersonationHooks";
import {useI18nLanguage} from "../hooks/i18nHooks";
import {VerifyEmailPage} from "./pages/VerifyEmailPage";
import {OrderPage} from "./pages/OrderPage";
import {OrderDetailsView} from "../components/orders/OrderDetailsView";
import {AdminFieldChangesView} from "./admin/AdminFieldChangesView";
import {DEFAULT_USER_PREFERENCES, languageTypeToLocale} from "../utils/userUtils";
import {UserPreferences} from "../types/domain/user";
import {useLoadableValueOrDefault} from "../hooks/jotaiHooks";
import {authenticatedUserPreferencesLoadable} from "../store/authenticatedUserStore";
import {ApiV1SwaggerPage} from "./pages/ApiV1SwaggerPage";
import {AdminAnnouncementsView} from "./admin/AdminAnnouncementsView";
import "mapbox-gl/dist/mapbox-gl.css";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";
import {Provider} from "jotai";

import "dayjs/locale/fr";
import "dayjs/locale/es";
import "dayjs/locale/nl";
import "dayjs/locale/ar";
import "dayjs/locale/de";
import "dayjs/locale/it";
import "dayjs/locale/pt";
import "dayjs/locale/hi";
import {AdminFeatureFlagsView} from "./admin/AdminFeatureFlagsView";
import {ApiV2SwaggerPage} from "./pages/ApiV2SwaggerPage";
import {isLoggingOutAtom} from "../store/authenticationStore.ts";
import {queryClient} from "../utils/queryClient.ts";

// Lazy-load DevTools only in development
const DevTools =
    process.env.NODE_ENV === "development"
        ? lazy(() => import("jotai-devtools").then((module) => ({default: module.DevTools})))
        : () => null;

i18next.on("languageChanged", function (lng) {
    dayjs.locale(lng);
});

const _App: React.FC = () => {
    useSetImpersonation();
    const isLoggingOut = useAtomValue(isLoggingOutAtom);
    const authentication = useAtomValue(authenticationLoadable);
    const authenticatedUserState = useAtomValue(authenticatedUserLoadable);
    const isAuthenticated = authentication.state === "hasData" && authenticatedUserState.state === "hasData";

    const authError = authentication.state === "hasError";
    const userError = authenticatedUserState.state === "hasError";

    let content: React.ReactNode;

    if (isLoggingOut) {
        content = <UnauthContent />;
    } else if (authError) {
        content = <UnauthContent />;
    } else if (userError) {
        content = <ErrorPage header="Error loading user profile. Try again in a few moments." />;
    } else if (isAuthenticated) {
        content = <AuthContent />;
    } else {
        content = null;
    }
    return (
        <>
            {process.env.NODE_ENV === "development" && (
                <Suspense fallback={null}>
                    <DevTools />
                </Suspense>
            )}
            {content}
        </>
    );
};

export const App: React.FC = () => {
    return (
        <Provider>
            <_App />
        </Provider>
    );
};

const AuthContent: React.FC = () => {
    useAnalyticsInit();
    useI18nLanguage();
    const userPreferences = useLoadableValueOrDefault<UserPreferences>(
        authenticatedUserPreferencesLoadable,
        DEFAULT_USER_PREFERENCES,
    );
    const locale = languageTypeToLocale(userPreferences?.language);
    const dateFormat = userPreferences.dateFormat;
    const globalDateFormat: Partial<AdapterFormats> = {
        normalDate: dateFormat,
        keyboardDate: dateFormat,
        fullDate: dateFormat,
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={locale} dateFormats={globalDateFormat}>
            <QueryClientProvider client={queryClient}>
                <Routes>
                    <Route path="/">
                        <Route path="" element={<Navigate replace to="insights" />} />
                        <Route path="insights" element={<InsightsPage />}>
                            <Route path="" element={<CompanyInsightsView />} />
                            <Route path=":companyId" element={<CompanyInsightsView />} />
                            <Route path=":companyId/farm/:farmId" element={<FarmInsightsView />} />
                            <Route path=":companyId/field/:fieldId" element={<FieldInsightsView />} />
                        </Route>
                        <Route path="map" element={<MapPage />}>
                            <Route path="" element={<CompanyInsightsView />} />
                            <Route path=":companyId" element={<MapView />} />
                            <Route path=":companyId/farm/:farmId" element={<MapView />} />
                            <Route path=":companyId/field/:fieldId" element={<MapView />} />
                        </Route>
                        <Route path="organization" element={<OrganizationPage />}>
                            <Route path="" element={<CompanyProfileView />} />
                            <Route path=":companyId" element={<CompanyProfileView />} />
                            <Route path=":companyId/users" element={<CompanyMembersView />} />
                            <Route path=":companyId/orders" element={<CompanyOrdersView />} />
                            <Route path=":companyId/farms" element={<CompanyFarmsView />} />
                        </Route>
                        <Route path="user" element={<UserPage />}>
                            <Route path=":userId" element={<UserDetailsView />} />
                            <Route path=":userId/companies" element={<UserCompaniesView />} />
                            <Route path=":userId/orders" element={<UserOrdersView />} />
                            <Route path=":userId/farms" element={<UserFarmsView />} />
                        </Route>
                        <Route path="company/:companyId/order/:orderId" element={<OrderPage />}>
                            <Route path="" element={<OrderDetailsView />} />
                        </Route>
                        <Route path="admin" element={<AdminPage />}>
                            <Route path="" element={<AdminDashboardView />} />
                            <Route path="users" element={<AdminUsersView />} />
                            <Route path="companies" element={<AdminCompaniesView />} />
                            <Route path="/admin/field-changes" element={<AdminFieldChangesView />} />
                            <Route path="/admin/feature-flags" element={<AdminFeatureFlagsView />} />
                            <Route path="/admin/announcements" element={<AdminAnnouncementsView />} />
                            <Route path="orders" element={<AdminOrdersView />}></Route>
                            <Route path="irritiles" element={<AdminIrriTilesView />} />
                            <Route path="irritiles/:irritileId" element={<AdminIrriTileDetailsView />} />
                            <Route path="farms" element={<AdminFarmsView />} />
                        </Route>
                        <Route path="invitation/:invitationId" element={<UserInvitationPage />} />
                        <Route path="orders/:companyId/:orderId/confirm" element={<OrderConfirmationPage />} />
                        <Route path="api-v1-swagger" element={<ApiV1SwaggerPage />} />
                        <Route path="api-v2-swagger" element={<ApiV2SwaggerPage />} />
                        <Route path="*" element={<ErrorPage header="Page not found" />} />
                    </Route>
                    <Route path="login" element={<Navigate replace to="/" />} />
                    <Route path="register" element={<Navigate replace to="/" />} />
                    <Route path="forgot-password" element={<Navigate replace to="/" />} />
                    <Route path="reset-password" element={<PasswordResetPage />} />
                </Routes>
            </QueryClientProvider>
        </LocalizationProvider>
    );
};

const UnauthContent: React.FC = () => {
    return (
        <Routes>
            <Route path="login" element={<LoginPage />} />
            <Route path="register" element={<RegistrationPage />} />
            <Route path="forgot-password" element={<PasswordRestorePage />} />
            <Route path="reset-password" element={<PasswordResetPage />} />
            <Route path="verify-email" element={<VerifyEmailPage />} />
            <Route path="*" element={<Navigate replace to="/login" />} />
        </Routes>
    );
};
